import {Observable, Subscription, ConnectableObservable} from 'rxjs';
import {map, publishReplay, first, switchMap, take} from 'rxjs/operators';
import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {HttpParams} from '@angular/common/http';
import {SupportedLocaleConfiguration} from './supportedlocaleconfiguration';
import {Router} from '@angular/router';
import {FilterResult} from '../../resource/filterresult';
import {BaseApiService} from '../../core/services/base-api.service';
import {Endpoint} from '../../layout/endpoint';

@Injectable()
export class SupportedLocaleConfigurationService extends BaseApiService implements OnDestroy {
  private supportedLocaleConfiguration: ConnectableObservable<SupportedLocaleConfiguration>;
  private _subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private _ngZone: NgZone,
  ) {
    super();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  public loadSupportedLocaleConfigurations() {
    if (!this.supportedLocaleConfiguration) {
      this.supportedLocaleConfiguration = this.getSupportedLocaleConfigurations().pipe(
        first(),
        publishReplay(1),
      ) as ConnectableObservable<any>;
      this._ngZone.run(() => {
        this._subscriptions.push(this.supportedLocaleConfiguration.connect());
      });
    }
  }

  public getSupportedLocaleConfiguration(): Observable<SupportedLocaleConfiguration> {
    // Initialize if not already done
    if (!this.supportedLocaleConfiguration) {
      // Create the observable but don't connect yet - let StartupApiService handle connection
      this.supportedLocaleConfiguration = this.getSupportedLocaleConfigurations().pipe(
        first(),
        publishReplay(1),
      ) as ConnectableObservable<any>;
    }
    return this.supportedLocaleConfiguration;
  }

  private getSupportedLocaleConfigurations(): Observable<SupportedLocaleConfiguration> {
    const attributes = [
      this.getAttr('ObjectID'),
      this.getAttr('DisplayName'),
      this.getAttr('Description'),
      this.getAttr('SupportedLanguageCode'),
    ].filter(attr => attr !== null);

    const params = new HttpParams()
      .append('filter', '/' + this.getType('SupportedLocaleConfiguration'))
      .append('attributes', attributes.join(','))
      .append('pageSize', '1');

    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        const options = {
          headers: this.headers,
          params: params,
          ...(!env.isCloud() ? {withCredentials: true} : {}),
        };
        return this.http.get<FilterResult>(env.getUrl() + 'v2/resources/', options);
      }),
      map(response => this.toSupportedLocaleConfiguration(response.Results)),
    );
  }

  private toSupportedLocaleConfiguration(uiconfs: any): SupportedLocaleConfiguration {
    if (Array.isArray(uiconfs) && uiconfs.length == 1) {
      return this.toSingleSupportedLocaleConfiguration(uiconfs[0]);
    } else {
      console.error('UIConfigurationService::toUIConfiguration invalid attribute given.');
      return null;
    }
  }

  private toSingleSupportedLocaleConfiguration(r: any): SupportedLocaleConfiguration {
    return <SupportedLocaleConfiguration>{
      id: r[this.getAttr('ObjectID')],
      displayname: r[this.getAttr('DisplayName')],
      description: r[this.getAttr('Description')],
      supportedlanguagecode: r[this.getAttr('SupportedLanguageCode')],
    };
  }

  private static handleError(error: any) {
    console.error(error);
    throw error;
  }

  private static handleErrorSilently(error: any) {
    console.error('Ignored error ' + JSON.stringify(error));
    return [];
  }
}
